class EconoSmartPredictiveSearch {
  constructor() {
    this.drawer = document.querySelector('[data-search-drawer]');
    if (!this.drawer) return;

    this.input = this.drawer.querySelector('[data-predictive-search-input]');
    this.results = this.drawer.querySelector('[data-predictive-search-results]');
    this.limit = Number(this.drawer.dataset.predictiveLimit) || 6;
    this.types = this.drawer.dataset.predictiveTypes || 'product,collection,page,article';
    this.initialHTML = this.results?.innerHTML || '';

    this.abortController = null;
    this.debounceTimer = null;
    this.lastQuery = '';
    this.openTrigger = null;

    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('click', (event) => {
      const open = event.target.closest('[data-search-open]');
      const close = event.target.closest('[data-search-close]');

      if (open) {
        event.preventDefault();
        this.open(open);
      }

      if (close) {
        event.preventDefault();
        this.close();
      }
    });

    this.input?.addEventListener('input', () => {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = window.setTimeout(() => this.search(), 180);
    });

    document.addEventListener('keydown', (event) => {
      if (!this.drawer.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    });
  }

  open(trigger) {
    this.openTrigger = trigger;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.input?.setAttribute('aria-expanded', 'true');
    this.openTrigger?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('drawer-open');

    window.setTimeout(() => {
      this.input?.focus();
      if ((this.input?.value || '').trim().length >= 2) this.search();
    }, 80);
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.input?.setAttribute('aria-expanded', 'false');
    this.openTrigger?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('drawer-open');
    this.abortController?.abort();
    this.openTrigger?.focus();
  }

  trapFocus(event) {
    const panel = this.drawer.querySelector('.search-drawer__panel');
    if (!panel) return;

    const focusable = Array.from(
      panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  reset() {
    this.abortController?.abort();
    this.lastQuery = '';
    if (this.results) {
      this.results.innerHTML = this.initialHTML;
      this.results.removeAttribute('aria-busy');
    }
  }

  async search() {
    const query = this.input?.value.trim() || '';

    if (query.length < 2) {
      this.reset();
      return;
    }

    if (query === this.lastQuery) return;

    this.lastQuery = query;
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.results?.setAttribute('aria-busy', 'true');

    const root = window.EconoSmart?.routes?.root || '/';
    const url = new URL(`${root}search/suggest`, window.location.origin);

    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', this.types);
    url.searchParams.set('resources[limit]', String(this.limit));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
    url.searchParams.set('section_id', 'predictive-search');

    try {
      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
        headers: { Accept: 'text/html' }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const wrapper = parsed.querySelector('#shopify-section-predictive-search');
      const content = wrapper?.innerHTML || html;

      if (this.results) this.results.innerHTML = content;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('EconoSmart predictive search failed:', error);

        if (this.results) {
          this.results.innerHTML = `
            <div class="predictive-results__empty">
              No pudimos cargar las sugerencias.
              <a href="${window.EconoSmart?.routes?.search || '/search'}?q=${encodeURIComponent(query)}">
                Ver búsqueda completa
              </a>
            </div>
          `;
        }
      }
    } finally {
      this.results?.removeAttribute('aria-busy');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new EconoSmartPredictiveSearch();
});
